import React from "react";
import ReactDOM from "react-dom";
import "./index.css";
import App, { browserHistory } from "./App";
import * as Sentry from "@sentry/react";
import { VERSION } from "./Version";
import { dsn } from './Sentry';
import { createRoot } from 'react-dom/client';
import { installFatalErrorTrapInDev } from "./Util";
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';

installFatalErrorTrapInDev();


const host = window.location.host;
let environment = 'production';
let datadogEnvironment = 'prod';
if (host?.includes('local.dev') || host?.includes('goat-mamba') || host?.includes('.local')) {
  environment = 'development';
  datadogEnvironment = 'dev';
}

Sentry.init({
  dsn,
  debug: false,
  environment,
  ignoreErrors: [
    'Non-Error promise rejection captured',
    /Shopify root is null/, // Facebook/Instagram IAB injected script error
  ],
  integrations: [Sentry.reactRouterV5BrowserTracingIntegration({
    history: browserHistory
  })],
  tracesSampleRate: 1.0,
  release: "aidkit@" + VERSION
});

function scrubTokenFromUrl(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.searchParams.delete('token');
    return parsed.toString();
  } catch {
    return url;
  }
}

// This can be used in local development to test Datadog RUM, but should be kept prod-only by default.
if (datadogEnvironment === 'prod') {
  datadogRum.init({
    applicationId: '2f39dc92-6fd9-4124-8427-3fcb96663abe',
    clientToken: 'pub8b683e8a4dd231723226067d890f5a59',
    site: 'datadoghq.com',
    service: 'aidkit-frontend',
    env: datadogEnvironment,
    version: VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackResources: true,
    trackUserInteractions: true,
    enablePrivacyForActionName: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask',
    allowedTracingUrls: ['https://aidkit-api.aidkit.org'],
    beforeSend: (event) => {
      switch (event.type) {
        case 'view':
          if (event.view.performance?.lcp?.resource_url) {
            event.view.performance.lcp.resource_url = scrubTokenFromUrl(event.view.performance.lcp.resource_url);
          }
          break;
        case 'resource':
          event.resource.url = scrubTokenFromUrl(event.resource.url);
          break;
        case 'error':
          if (event.error.resource?.url) {
            event.error.resource.url = scrubTokenFromUrl(event.error.resource.url);
          }
          break;
        case 'long_task':
          if (event.long_task.scripts) {
            for (const script of event.long_task.scripts) {
              if (script.source_url) {
                script.source_url = scrubTokenFromUrl(script.source_url);
              }
            }
          }
          break;
      }
      return true;
    },
    plugins: [reactPlugin({ router: false })],
  });

  datadogRum.setViewName(`${browserHistory.location.pathname}${browserHistory.location.search}${browserHistory.location.hash}`);
  browserHistory.listen((location) => {
    datadogRum.setViewName(`${location.pathname}${location.search}${location.hash}`);
  });
}
const container = document.getElementById("root")!;
const root = createRoot(container);
root.render(<App />);
